import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import CenteredFallback from "../components/CenteredFallback/CenteredFallback"
import Loader from "../components/Loader/Loader"
import views from "../data/all-views.json"
import {
  getDataConfigsUrl,
  getWantedSite,
  getDefaultLanguage,
  getEnabledLanguages,
} from "../helpers/general"
import { setSiteConfig } from "../hooks/use-site-config"
import locales from "../locales"
import { CasUserContext } from "./CasUserContext"

export const AppContext = React.createContext(null)

const enabledLanguages = getEnabledLanguages(locales)
const defaultLanguage = getDefaultLanguage(locales)

const dataUrl = getDataConfigsUrl()

// Do cache busting of config files every time the site gets loaded
const cacheBustVersion = `?v=${Date.now()}`

let configFiles = {}

export const ContextWrapper = props => {
  const initialState = initState()

  const [store, setStore] = useState(initialState)

  const [ready, setReady] = useState(false)

  const casUserContext = useContext(CasUserContext)

  //Get default locale if need redirect
  const { i18n } = useTranslation()
  const localeCodes = Object.keys(enabledLanguages)
  const locale = localeCodes.includes(i18n.language)
    ? i18n.language
    : defaultLanguage

  const wantedData = getWantedSite()
  const wantedSite = wantedData.wantedSite
  const needRedirect = wantedData.redirect

  useEffect(() => {
    fetchIndex().then(data => {
      let site = null
      data.forEach(element => {
        if (element.code === wantedSite.toLowerCase()) {
          site = element
        }
      })

      //If wanted site is not valid site, set site = Ambiences
      if (!site) {
        site = data.find(item => item.code === "ambiences")
      }

      setSiteConfig(site)

      //Fill config files routes
      configFiles = {
        categories:
          site.configurationFilesPath +
          "/category-tree.json" +
          cacheBustVersion,
        variables:
          site.configurationFilesPath +
          "/all-variables.json" +
          cacheBustVersion,
        incompatibilities:
          site.configurationFilesPath +
          "/no-compatibilities.json" +
          cacheBustVersion,
        predesigns:
          site.configurationFilesPath + "/pre-designs.json" + cacheBustVersion,
        defaults:
          site.configurationFilesPath +
          "/default-values.json" +
          cacheBustVersion,
      }

      setStore(prevStore => {
        return { ...prevStore, ...{ initConfig: site } }
      })

      fetchConfigs().then(data => {
        const processedCategories = processCategories(data)

        const processedData = { ...data, categories: processedCategories }

        setStore(prevStore => {
          return { ...prevStore, ...processedData }
        })
        if (needRedirect) {
          //Redirect
          window.location.href = `/${locale}/${site.url}`
        }
        setReady(true)

        if (casUserContext.withAuth && !casUserContext.user) {
          return false
        }
      })
    })
  }, [
    casUserContext.user,
    casUserContext.withAuth,
    locale,
    needRedirect,
    wantedSite,
  ])

  if (!ready)
    return (
      <CenteredFallback>
        <Loader />
      </CenteredFallback>
    )

  return (
    <AppContext.Provider value={{ store }}>
      {props.children}
    </AppContext.Provider>
  )
}

const initState = () => {
  let obj = {}

  for (const property in configFiles) {
    obj[property] = null
  }

  // Load views from imported local file
  obj["views"] = views

  return obj
}

// Get variables for each category
const processCategories = data => {
  if (!data || !data.categories) {
    return null
  }

  const processed = Object.entries(data.categories).map(category =>
    processCategory(category, data.variables)
  )

  return Object.fromEntries(processed)
}

const processCategory = (category, allVariables) => {
  const [categoryId, categoryData] = category

  if (Object.entries(categoryData.HIJOS).length <= 0) {
    const variables = getCategoryVariables(category, allVariables)

    const processedCategory = [
      categoryId,
      { ...categoryData, VARIABLES: variables },
    ]

    return processedCategory
  }

  const processedHijos = Object.entries(categoryData.HIJOS).map(childCategory =>
    processCategory(childCategory, allVariables)
  )

  const processedCategory = [
    categoryId,
    { ...categoryData, HIJOS: Object.fromEntries(processedHijos) },
  ]

  return processedCategory
}

const getCategoryVariables = (category, allVariables) => {
  const [categoryId] = category

  const variables = Object.entries(allVariables).filter(variable => {
    const [, variableData] = variable

    return variableData.CATEGORIA === categoryId
  })

  return Object.fromEntries(variables)
}

const fetchIndex = async () => {
  const response = await fetch("/data/index.json")
  const data = await response.json()
  return data
}

const fetchConfigs = async () => {
  const allData = await Promise.all(
    Object.keys(configFiles).map(async key => {
      const fileName = configFiles[key]
      const url = `${dataUrl}${fileName}`

      const response = await fetch(url)
      const data = await response.json()

      return { [key]: data }
    })
  )

  return allData.reduce((accumulator, currentValue) => {
    return { ...accumulator, ...currentValue }
  })
}
